import { useCallback, useMemo, useRef, useState } from "react";
import {
  getDocs,
  onSnapshot,
  limit,
  orderBy,
  query,
  startAfter,
} from "../firebase";
import { filterUndefinedValues, uniqueVals } from "@markit/common.utils";
import { AccountData, TeamMember } from "@markit/common.types";
import { getUserTeamMembersRef } from "../utils/FirebaseUtils";
import { fetchSingleUser } from "../utils/FetchSingleData";
import { batch, useDispatch, useSelector } from "react-redux";
import { accountActions, getAccountState } from "../redux/slices/accountSlice";
import useOnUnmount from "./useOnUnmount";

type useLoadUserTeamMembersListProps = {
  userId: string;
  windowSize: number;
};
export const useLoadUserTeamMembersList = (
  props: useLoadUserTeamMembersListProps
) => {
  const { userId, windowSize } = props;

  const { followingAccountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();

  const [fetchedUserData, setFetchedUserData] = useState<AccountData[]>([]);
  const [fetchedTeamMemberData, setFetchedTeamMemberData] = useState<
    TeamMember[]
  >([]);
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [lastVisible, setLastVisible] = useState<string>(
    new Date().toISOString()
  );
  const [loadedAllMessages, setLoadedAllMessages] = useState(false);

  useOnUnmount(
    (latestDeps) => {
      batch(() => {
        dispatch(
          accountActions.addMultipleFollowingAccountData(
            latestDeps[0] as AccountData[]
          )
        );
      });
      unsubscribeArrayRef.current.forEach(
        (unsubscribe) => unsubscribe && unsubscribe()
      );
      unsubscribeArrayRef.current = []; // Clear the array after cleanup
    },
    [fetchedUserData]
  );

  const addUserData = useCallback((newUserData: AccountData[]) => {
    setFetchedUserData((fetchedUserData) =>
      uniqueVals(
        fetchedUserData.concat(newUserData),
        (userData) => userData.uid
      )
    );
  }, []);

  const removeUserData = useCallback((userDataUid: string) => {
    setFetchedUserData((fetchedUserData) => {
      const index = fetchedUserData.findIndex(
        (fetched) => fetched.uid === userDataUid
      );
      if (index !== -1) {
        fetchedUserData.splice(index, 1);
      }
      return [...fetchedUserData];
    });
  }, []);

  const addTeamMemberData = useCallback((newTeamMembers: TeamMember[]) => {
    setFetchedTeamMemberData((fetchedTeamMemberData) =>
      uniqueVals(
        newTeamMembers.concat(fetchedTeamMemberData),
        (teamMemberData) => teamMemberData.uid
      ).sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
    );
  }, []);

  const removeTeamMemberData = useCallback((newTeamMemberUid: string) => {
    setFetchedTeamMemberData((fetchedTeamMemberData) => {
      const index = fetchedTeamMemberData.findIndex(
        (fetched) => fetched.uid === newTeamMemberUid
      );
      if (index !== -1) {
        fetchedTeamMemberData.splice(index, 1);
      }
      return [...fetchedTeamMemberData];
    });
  }, []);

  const isFinished = useMemo(() => loadedAllMessages, [loadedAllMessages]);

  const updateData = useCallback(
    async (foundMemberData: TeamMember[]) => {
      addTeamMemberData(foundMemberData);
      const membersUserData = await Promise.all(
        foundMemberData.map((member) =>
          fetchSingleUser(member.uid, followingAccountData)
        )
      );
      const definedMembersUserData: AccountData[] =
        filterUndefinedValues(membersUserData);
      addUserData(definedMembersUserData);
    },
    [addTeamMemberData, addUserData, followingAccountData]
  );

  const initialUpdateData = useCallback(
    async (foundMemberData: TeamMember[]) => {
      if (foundMemberData.length) {
        setLastVisible(foundMemberData[foundMemberData.length - 1].createdAt);
      }
      if (foundMemberData.length < windowSize) {
        setLoadedAllMessages(true);
      }

      if (foundMemberData.length > 0) {
        await updateData(foundMemberData);
      }
    },
    [updateData, windowSize]
  );

  const liveUpdateData = useCallback(
    async (
      type: "added" | "modified" | "removed",
      member: TeamMember,
      foundMemberData: TeamMember[]
    ) => {
      if (
        (type === "added" &&
          !foundMemberData.some(
            (memberData) => memberData.uid === member.uid
          )) ||
        type === "modified"
      ) {
        updateData([member]);
      } else if (type === "removed") {
        removeTeamMemberData(member.uid);
        removeUserData(member.uid);
      }
    },
    [removeTeamMemberData, removeUserData, updateData]
  );

  const loadUsers = useCallback(async () => {
    if (!isFinished && !isLoading) {
      setIsLoading(true);
      const teamMembersRef = getUserTeamMembersRef(userId);
      const teamMembersQuery = query(
        teamMembersRef,
        orderBy("createdAt", "desc"),
        limit(windowSize)
      );

      const foundTeamMembers = (await getDocs(teamMembersQuery)).docs.map(
        (doc) => doc.data()
      );
      await initialUpdateData(foundTeamMembers);

      const unsubscribe = onSnapshot(teamMembersQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          const memberData = message.doc.data();
          liveUpdateData(message.type, memberData, foundTeamMembers);
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);

      setIsLoading(false);
    }
  }, [
    isFinished,
    isLoading,
    userId,
    windowSize,
    initialUpdateData,
    liveUpdateData,
  ]);

  const loadMoreUsers = useCallback(async () => {
    if (!isFinished && !isLoading) {
      setIsLoading(true);
      const teamMembersRef = getUserTeamMembersRef(userId);
      const teamMembersQuery = query(
        teamMembersRef,
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(windowSize)
      );

      const foundTeamMembers = (await getDocs(teamMembersQuery)).docs.map(
        (doc) => doc.data()
      );
      await initialUpdateData(foundTeamMembers);
      const unsubscribe = onSnapshot(teamMembersQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          const memberData: TeamMember = message.doc.data();
          liveUpdateData(message.type, memberData, foundTeamMembers);
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);

      setIsLoading(false);
    }
  }, [
    isFinished,
    isLoading,
    userId,
    lastVisible,
    windowSize,
    initialUpdateData,
    liveUpdateData,
  ]);

  return {
    isFinished,
    isLoading,
    fetchedUserData,
    fetchedTeamMemberData,
    loadUsers,
    loadMoreUsers,
  };
};
